import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Store, Save, Loader2, CheckCircle, XCircle, AlertTriangle, Link2, Unlink, CreditCard, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import PlatformAiProvidersSection from '@/components/admin/PlatformAiProvidersSection';
import PlatformCredentialsSection from '@/components/admin/PlatformCredentialsSection';
import AiAdapterSpecsSection from '@/components/admin/AiAdapterSpecsSection';
import PlatformSpendCapsSection from '@/components/admin/PlatformSpendCapsSection';

interface AlibabaInfo {
  configured: boolean;
  source: 'platform_config' | 'environment' | 'none';
  app_key_masked: string;
  updated_at: string | null;
  has_platform_credentials: boolean;
  has_env_credentials: boolean;
  shared_platform_db: boolean;
}

export default function PlatformSettings() {
  const user = useAuthStore((s) => s.user);

  if (!user?.isPlatformAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-900 font-medium">Platform admin access required.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="flex items-center gap-3 mb-2">
        <Globe className="w-7 h-7 text-sage-700" />
        <h1 className="text-2xl font-bold text-stone-900">Platform Settings</h1>
      </div>
      <p className="text-stone-500 mb-8">
        Settings on this page apply to every site on the platform. Individual sites can still override these values in their own General Settings.
      </p>

      <PlatformAiProvidersSection />
      <PlatformSpendCapsSection />
      <PlatformCredentialsSection />
      <AiAdapterSpecsSection />
      <PlatformAlibabaCredentials />
      <AlibabaOauthConnection />
      <WorkspacesLink />
    </div>
  );
}

function WorkspacesLink() {
  return (
    <Link
      to="/admin/platform/workspaces"
      className="block mt-6 group"
    >
      <Card className="p-5 hover:border-sage-300 hover:shadow-sm transition-all">
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-sage-700" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-stone-900 group-hover:text-sage-800">
              Workspaces
            </h2>
            <p className="text-sm text-stone-500">
              Browse Rumio customers and override their subscription tier without touching Stripe.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-sage-700" />
        </div>
      </Card>
    </Link>
  );
}

interface OauthStatus {
  connected: boolean;
  expires_at?: string;
  scope?: string;
  merchant_id?: string;
  connected_at?: string;
  last_refreshed_at?: string;
  expires_in_seconds?: number;
  health: 'healthy' | 'expiring_soon' | 'expired' | 'disconnected';
}

function AlibabaOauthConnection() {
  const [status, setStatus] = useState<OauthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = () => {
    setLoading(true);
    api.get<OauthStatus>('/admin/platform/settings/alibaba/status')
      .then(setStatus)
      .catch(() => setMessage('Failed to load connection status'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function handleConnect() {
    setBusy(true);
    setMessage('');
    try {
      const res = await api.post<{ url: string }>('/admin/platform/settings/alibaba/oauth-url', {
        redirect_uri: window.location.origin + '/callback',
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        setMessage('No authorization URL returned');
        setBusy(false);
      }
    } catch (err: any) {
      setMessage(err?.message || 'Failed to start OAuth flow');
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect the Alibaba account? Dippy will stop using the API until you reconnect.')) return;
    setBusy(true);
    setMessage('');
    try {
      await api.delete('/admin/platform/settings/alibaba/disconnect');
      setMessage('Disconnected.');
      load();
    } catch (err: any) {
      setMessage(err?.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Card className="p-5">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </Card>
    );
  }

  const healthBadge = (() => {
    if (!status?.connected) {
      return { label: 'Not connected', cls: 'bg-stone-100 text-stone-600', Icon: XCircle };
    }
    if (status.health === 'expired') {
      return { label: 'Token expired', cls: 'bg-red-100 text-red-800', Icon: XCircle };
    }
    if (status.health === 'expiring_soon') {
      return { label: 'Expiring soon', cls: 'bg-amber-100 text-amber-800', Icon: AlertTriangle };
    }
    return { label: 'Connected', cls: 'bg-green-100 text-green-800', Icon: CheckCircle };
  })();

  return (
    <Card className="p-5 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link2 className="w-5 h-5 text-sage-700" />
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Alibaba Account Connection</h2>
            <p className="text-sm text-stone-500">
              OAuth link between Dippy and an Alibaba.com buyer account. Required before Dippy can call any seller API.
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${healthBadge.cls}`}>
          <healthBadge.Icon className="w-3.5 h-3.5" />
          {healthBadge.label}
        </span>
      </div>

      {status?.connected && (
        <div className="mb-4 text-sm text-stone-600 space-y-1">
          {status.merchant_id && <p>Merchant: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{status.merchant_id}</code></p>}
          {status.scope && <p>Scope: <span className="text-stone-500">{status.scope}</span></p>}
          {status.connected_at && <p>Connected: {new Date(status.connected_at).toLocaleString()}</p>}
          {status.expires_at && <p>Access token expires: {new Date(status.expires_at).toLocaleString()}</p>}
          {status.last_refreshed_at && <p>Last refreshed: {new Date(status.last_refreshed_at).toLocaleString()}</p>}
        </div>
      )}

      {!status?.connected && (
        <p className="text-sm text-stone-500 mb-4">
          Click <strong>Connect Alibaba</strong> below to open Alibaba's authorization page. After approving, you'll be redirected back here and the connection will be active.
        </p>
      )}

      <div className="flex items-center gap-3">
        {!status?.connected ? (
          <Button onClick={handleConnect} disabled={busy} size="sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
            Connect Alibaba
          </Button>
        ) : (
          <>
            <Button onClick={handleConnect} disabled={busy} variant="outline" size="sm">
              <Link2 className="w-4 h-4 mr-1" />
              Reconnect
            </Button>
            <Button onClick={handleDisconnect} disabled={busy} variant="outline" size="sm">
              <Unlink className="w-4 h-4 mr-1" />
              Disconnect
            </Button>
          </>
        )}
        {message && (
          <span className={`text-sm ${message.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>
    </Card>
  );
}

function PlatformAlibabaCredentials() {
  const [info, setInfo] = useState<AlibabaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [message, setMessage] = useState('');

  const load = () => {
    setLoading(true);
    api.get<{ alibaba: AlibabaInfo }>('/admin/platform/settings/marketplace-credentials')
      .then((res) => { setInfo(res.alibaba); })
      .catch(() => { setMessage('Failed to load platform credentials'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function handleSave() {
    setSaving(true);
    setMessage('');
    try {
      const res = await api.put<{ alibaba: AlibabaInfo }>('/admin/platform/settings/marketplace-credentials', {
        alibaba_app_key: appKey || undefined,
        alibaba_app_secret: appSecret || undefined,
      });
      setInfo(res.alibaba);
      setAppKey('');
      setAppSecret('');
      setMessage('Credentials saved — applies to every site.');
      // Reload to pick up updated_at
      load();
    } catch {
      setMessage('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!confirm('Remove platform-level Alibaba credentials? Sites with their own credentials are unaffected. Sites without their own will fall back to environment variables (if set).')) return;
    setSaving(true);
    setMessage('');
    try {
      await api.delete('/admin/platform/settings/marketplace-credentials');
      setMessage('Platform credentials cleared.');
      load();
    } catch {
      setMessage('Failed to clear credentials');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card className="p-5">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </Card>
    );
  }

  const configured = info?.configured;
  const source = info?.source;

  return (
    <Card className="p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Store className="w-5 h-5 text-sage-700" />
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Alibaba Open Platform API</h2>
            <p className="text-sm text-stone-500">Default credentials used by Dippy on every site, unless the site sets its own.</p>
          </div>
        </div>
        {configured ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3.5 h-3.5" />
            {source === 'environment' ? 'From environment' : 'Platform configured'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-600">
            <XCircle className="w-3.5 h-3.5" />
            Not configured
          </span>
        )}
      </div>

      {info?.app_key_masked && (
        <p className="text-sm text-stone-500 mb-3">
          Current key: <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{info.app_key_masked}</code>
          {info.updated_at && (
            <span className="ml-2 text-stone-400">· updated {new Date(info.updated_at).toLocaleString()}</span>
          )}
        </p>
      )}

      {!info?.shared_platform_db && (
        <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Platform DB not configured</p>
            <p className="text-amber-800">
              <code>PLATFORM_DATABASE_URL</code> is not set on this container. Credentials you save here will only apply to the current tenant, not platform-wide. Set the env var on every tenant container to enable true platform-level sharing.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Input
          label="App Key"
          value={appKey}
          onChange={(e) => setAppKey(e.target.value)}
          placeholder={info?.has_platform_credentials ? 'Leave empty to keep current' : 'Enter Alibaba App Key'}
          type="password"
        />
        <Input
          label="App Secret"
          value={appSecret}
          onChange={(e) => setAppSecret(e.target.value)}
          placeholder={info?.has_platform_credentials ? 'Leave empty to keep current' : 'Enter Alibaba App Secret'}
          type="password"
        />
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving || (!appKey && !appSecret)} size="sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Platform Credentials
        </Button>
        {info?.has_platform_credentials && (
          <Button onClick={handleClear} disabled={saving} variant="outline" size="sm">
            Clear
          </Button>
        )}
        {message && (
          <span className={`text-sm ${message.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>
    </Card>
  );
}
